import AppShell from '@/components/AppShell'
import { get, set } from '@/lib/storage'
import { useMemo, useState } from 'react'
import { useNotifications } from '@/lib/notifications'
import { logAudit } from '@/lib/audit'

type Template = {
  id: string
  name: string
  mill: string
  delimiter: string
  headerRow: boolean
  dateFormat: string
  mapping: { field: string; column: number }[]
}

const TK = 'import-templates'

export default function ImportTemplates() {
  const [list, setList] = useState<Template[]>(get<Template[]>(TK, []))
  const [tpl, setTpl] = useState<Template>({
    id: crypto.randomUUID(),
    name: '',
    mill: '',
    delimiter: ',',
    headerRow: true,
    dateFormat: 'M/d/yy',
    mapping: []
  })
  const { push } = useNotifications()
  const saveList = (next: Template[]) => {
    setList(next)
    set(TK, next)
  }

  function resetNew() {
    setTpl({
      id: crypto.randomUUID(),
      name: '',
      mill: '',
      delimiter: ',',
      headerRow: true,
      dateFormat: 'M/d/yy',
      mapping: []
    })
  }
  function addMap() {
    setTpl(t => ({ ...t, mapping: [...t.mapping, { field: '', column: 0 }] }))
  }
  function setMap(i: number, k: 'field' | 'column', v: any) {
    setTpl(t => ({
      ...t,
      mapping: t.mapping.map((m, ix) => (ix === i ? { ...m, [k]: k === 'column' ? Number(v) : v } : m))
    }))
  }
  function removeMap(i: number) {
    setTpl(t => ({ ...t, mapping: t.mapping.filter((_, ix) => ix !== i) }))
  }

  // --- Mapping validator (duplicates + missing) ---
  const { dupFields, dupCols, missingFields, missingCols } = useMemo(() => {
    const df = new Set<string>()
    const dc = new Set<number>()
    const mf = new Set<number>() // indexes with empty field
    const mc = new Set<number>() // indexes with missing/NaN column

    tpl.mapping.forEach((m, i) => {
      const fieldEmpty = !m.field || !m.field.trim()
      const colInvalid = m.column === undefined || m.column === null || Number.isNaN(Number(m.column))
      if (fieldEmpty) mf.add(i)
      if (colInvalid) mc.add(i)

      if (m.field && tpl.mapping.findIndex(x => x.field === m.field) !== i) df.add(m.field)
      if (!colInvalid && tpl.mapping.findIndex(x => x.column === m.column) !== i) dc.add(m.column)
    })

    return { dupFields: df, dupCols: dc, missingFields: mf, missingCols: mc }
  }, [tpl.mapping])

  function saveTpl() {
    if (!tpl.name.trim()) {
      push({ title: 'Template needs a name', level: 'warning' })
      return
    }
    if (dupFields.size || dupCols.size) {
      push({
        title: 'Fix duplicate mappings',
        body: 'Resolve duplicate fields/columns before saving.',
        level: 'warning'
      })
      return
    }
    if (missingFields.size || missingCols.size) {
      push({
        title: 'Incomplete mappings',
        body: 'Some rows have empty field names or invalid column numbers.',
        level: 'warning'
      })
      return
    }

    const next = [tpl, ...list.filter(x => x.id !== tpl.id)]
    saveList(next)
    push({ title: 'Import template saved', body: `${tpl.name} (${tpl.mill || 'Unassigned mill'})` })
    logAudit({ action: 'save-import-template', details: tpl }, get('role', 'Admin'))
    resetNew()
  }
  function deleteTpl(id: string) {
    const next = list.filter(x => x.id !== id)
    saveList(next)
    push({ title: 'Import template removed' })
  }
  function loadTpl(t: Template) {
    setTpl(JSON.parse(JSON.stringify(t)))
  }

  return (
    <AppShell title="Import Templates">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        <div className="card">
          <div className="card-header">Template Editor</div>
          <div className="card-body grid gap-3">
            <input
              className="input"
              placeholder="Name"
              value={tpl.name}
              onChange={e => setTpl({ ...tpl, name: e.target.value })}
            />
            <input
              className="input"
              placeholder="Mill (name/id)"
              value={tpl.mill}
              onChange={e => setTpl({ ...tpl, mill: e.target.value })}
            />
            <div className="grid grid-cols-3 gap-3">
              <input
                className="input"
                placeholder="Delimiter"
                value={tpl.delimiter}
                onChange={e => setTpl({ ...tpl, delimiter: e.target.value })}
              />
              <input
                className="input"
                placeholder="Date format (e.g., M/d/yy or yyyy-MM-dd)"
                value={tpl.dateFormat}
                onChange={e => setTpl({ ...tpl, dateFormat: e.target.value })}
              />
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={tpl.headerRow}
                  onChange={e => setTpl({ ...tpl, headerRow: e.target.checked })}
                />
                Header row
              </label>
            </div>

            <div className="text-sm font-semibold">Field Mapping</div>
            <div className="grid gap-2">
              {(dupFields.size > 0 || dupCols.size > 0 || missingFields.size > 0 || missingCols.size > 0) && (
                <div className="text-xs text-red-600">
                  {dupFields.size > 0 && <>Duplicate fields: {[...dupFields].join(', ')}.</>}
                  {dupFields.size > 0 && (dupCols.size > 0 || missingFields.size > 0 || missingCols.size > 0) && ' '}
                  {dupCols.size > 0 && <>Duplicate columns: {[...dupCols].join(', ')}.</>}
                  {(dupFields.size > 0 || dupCols.size > 0) && (missingFields.size > 0 || missingCols.size > 0) && ' '}
                  {missingFields.size > 0 && <>Empty field names on rows: {[...missingFields].map(i => i + 1).join(', ')}.</>}
                  {missingFields.size > 0 && missingCols.size > 0 && ' '}
                  {missingCols.size > 0 && <>Invalid column # on rows: {[...missingCols].map(i => i + 1).join(', ')}.</>}
                </div>
              )}

              {tpl.mapping.map((m, i) => (
                <div key={i} className="grid grid-cols-6 gap-2">
                  <input
                    className={`input col-span-4 ${missingFields.has(i) ? 'ring-1 ring-red-300' : ''}`}
                    placeholder="Field (e.g., securityTag, deliveredAt, grossWeight…)"
                    value={m.field}
                    onChange={e => setMap(i, 'field', e.target.value)}
                  />
                  <input
                    className={`input col-span-1 ${missingCols.has(i) ? 'ring-1 ring-red-300' : ''}`}
                    type="number"
                    placeholder="Column #"
                    value={Number.isFinite(m.column) ? m.column : ''}
                    onChange={e => setMap(i, 'column', e.target.value)}
                  />
                  <button className="btn-secondary" onClick={() => removeMap(i)}>
                    Remove
                  </button>
                </div>
              ))}
              <button className="btn-secondary w-fit" onClick={addMap}>
                Add mapping
              </button>
            </div>

            <div className="flex gap-2">
              <button className="btn" onClick={saveTpl}>
                Save Template
              </button>
              <button className="btn-secondary" onClick={resetNew}>
                Reset
              </button>
            </div>

            <div className="text-xs text-slate-500">
              Common fields: <code>securityTag</code>, <code>scaleNumber</code>, <code>grossWeight</code>, <code>netTons</code>,{' '}
              <code>deliveredAt</code>, <code>destination</code>, <code>loggingCompany</code>, <code>truckingCompany</code>,{' '}
              <code>driver</code>. Date format supports <code>M/d/yy</code> (e.g., 9/18/25) or <code>yyyy-MM-dd</code>.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">My Templates</div>
          <div className="card-body">
            {list.length === 0 && <div className="text-sm text-slate-600">No templates yet.</div>}
            <div className="grid gap-2">
              {list.map(t => (
                <div key={t.id} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-slate-500">
                      {t.mill || 'Unassigned mill'} • delim “{t.delimiter}” • header {String(t.headerRow)} • date {t.dateFormat}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => loadTpl(t)}>
                      Edit
                    </button>
                    <button className="btn-secondary" onClick={() => deleteTpl(t.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </AppShell>
  )
}
